import { useState } from 'react';
import {
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';

import type { GameModel } from '@/features/games/types';

interface GameToggleProps {
  game: GameModel;
  onToggle: (available: boolean, game: GameModel) => Promise<void>;
}

const GameToggle = ({ game, onToggle }: GameToggleProps) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSwitchClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);
    await onToggle(!game.available, game);
    setLoading(false);
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
  };

  const actionText = game.available ? 'Disable' : 'Enable';
  const tooltipText = `${actionText} ${game.name || 'this game'}`;

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <FormControlLabel
          control={
            <Switch
              checked={game.available}
              onChange={handleSwitchClick}
              color={game.available ? 'success' : 'error'}
              disabled={loading}
            />
          }
          label=""
          sx={{ margin: 0 }}
        />
      </Tooltip>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        slotProps={{
          backdrop: {
            sx: { backdropFilter: 'blur(4px)' },
          },
        }}
      >
        <DialogTitle>Confirm Game Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {game.available ? 'disable' : 'enable'}{' '}
            {game.name || 'this game'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color={game.available ? 'error' : 'primary'}
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GameToggle;